import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface EULAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EULAModal({ isOpen, onClose }: EULAModalProps) {
  const { theme } = useTheme();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-black/75"></div>
        </div>

        <div 
          className={`inline-block w-full max-w-4xl p-6 my-8 text-left align-middle transition-all transform rounded-lg shadow-xl ${
            theme === 'dark' ? 'bg-navy-800' : 'bg-white'
          }`}
        >
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-3xl font-bold text-orange-500">End User License Agreement (EULA)</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <X size={24} />
            </button>
          </div>

          <div className={`overflow-y-auto max-h-[70vh] pr-4 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <p className="mb-4">Effective Date: 4/10/2025</p>
            
            <p className="mb-6">This End User License Agreement ("Agreement") is between you and Show Your Bits and governs your use of the Show Your Bits mobile application ("App"). By downloading or using the App, you agree to be bound by this Agreement.</p>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">1. License</h3>
              <p>You are granted a non-exclusive, non-transferable, revocable license to use the App for personal, non-commercial purposes strictly in accordance with this Agreement.</p>
            </section>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">2. Restrictions</h3>
              <p>You agree not to:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Reverse engineer, decompile, or modify the App.</li>
                <li>Use the App for any unlawful purpose or in violation of any applicable law or regulation.</li>
                <li>Rent, lease, or sublicense the App to others.</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">3. Ownership</h3>
              <p>The App and all rights, title, and interest in and to the App, including all intellectual property rights, are owned by ShowYourBits.</p>
            </section>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">4. User-Generated Content</h3>
              <p>If the App allows you to submit content, you retain ownership of that content but grant us a non-exclusive, royalty-free license to use, display, and distribute it within the App and associated services.</p>
            </section>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">5. Termination</h3>
              <p>This license is effective until terminated. It will terminate automatically without notice if you fail to comply with any term. Upon termination, you must cease all use of the App and delete all copies.</p>
            </section>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">6. No Warranty</h3>
              <p>The App is provided "as is" and "as available" without warranty of any kind. We disclaim all warranties, including implied warranties of merchantability and fitness for a particular purpose.</p>
            </section>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">7. Limitation of Liability</h3>
              <p>To the fullest extent permitted by law, ShowYourBits will not be liable for any damages arising out of or relating to the use of or inability to use the App.</p>
            </section>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">8. Updates</h3>
              <p>We may provide updates that must be installed to continue using the App. You agree to accept such updates as part of this license.</p>
            </section>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">9. Governing Law</h3>
              <p>This Agreement is governed by the laws of The United States of America, excluding its conflicts of law principles.</p>
            </section>
            
            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-orange-500">10. Contact</h3>
              <p>If you have any questions about this Agreement, you can contact us at:</p>
              <p className="mt-2">Email: showyourbits@protonmail.com</p>
            </section>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
